import { useRef, useState } from 'react'
import { deletePayAppAttachment } from '../../../api/attachments'
import { fetchPulseFileBlob, uploadChangeOrderAttachment } from '../../../features/pulse/pulseApi'

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp'

export interface CoAttachmentRow {
  id: number
  original_name: string | null
  filename: string
  file_size?: number | null
  mime_type?: string | null
  change_order_id?: number | null
}

function fileKey(name: string, size: number): string {
  return `${name}\0${size}`
}

function isImage(name: string, mime?: string | null): boolean {
  if (mime?.startsWith('image/')) return true
  return /\.(png|jpe?g|gif|webp)$/i.test(name)
}

function isPdf(name: string, mime?: string | null): boolean {
  if (mime === 'application/pdf') return true
  return /\.pdf$/i.test(name)
}

export default function CoAttachmentField({
  projectId,
  changeOrderId,
  attachments = [],
  chipsOnly = false,
  hideChips = false,
  onUploaded,
}: {
  projectId: number
  changeOrderId: number
  attachments?: CoAttachmentRow[]
  chipsOnly?: boolean
  hideChips?: boolean
  onUploaded?: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [preview, setPreview] = useState<{
    name: string
    url: string
    kind: 'pdf' | 'image' | 'file'
  } | null>(null)

  function isDuplicate(file: File): boolean {
    const key = fileKey(file.name, file.size)
    return attachments.some((a) =>
      fileKey(a.original_name ?? a.filename, Number(a.file_size || 0)) === key,
    )
  }

  async function onFile(file: File | undefined) {
    if (!file) return
    if (isDuplicate(file)) {
      setErr('That file is already attached to this change order')
      return
    }
    setBusy(true)
    setErr(null)
    try {
      await uploadChangeOrderAttachment(projectId, changeOrderId, file)
      onUploaded?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleRemove(attId: number) {
    if (!confirm('Remove this attachment?')) return
    setBusy(true)
    setErr(null)
    try {
      await deletePayAppAttachment(attId)
      onUploaded?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  async function openPreview(att: CoAttachmentRow) {
    const name = att.original_name ?? att.filename
    try {
      const blob = await fetchPulseFileBlob(`/api/projects/${projectId}/pulse/attachments/${att.id}/download`)
      const url = URL.createObjectURL(blob)
      const kind = isPdf(name, att.mime_type) ? 'pdf' : isImage(name, att.mime_type) ? 'image' : 'file'
      setPreview({ name, url, kind })
    } catch {
      setErr('Could not open file')
    }
  }

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url)
    setPreview(null)
  }

  const chips = !hideChips && attachments.length > 0 ? (
    <span
      style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}
      data-testid={`co-attach-chips-${changeOrderId}`}
    >
      {attachments.map((att) => (
        <span
          key={att.id}
          className="chip c-gry"
          style={{ fontSize: 10, maxWidth: 160, display: 'inline-flex', alignItems: 'center' }}
          data-testid={`co-attach-chip-${changeOrderId}-${att.id}`}
        >
          <button
            type="button"
            onClick={() => { void openPreview(att) }}
            title={`Preview ${att.original_name ?? att.filename}`}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontSize: 10,
              maxWidth: 120,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'inherit',
            }}
          >
            {att.original_name ?? att.filename}
          </button>
          <button
            type="button"
            aria-label="Remove attachment"
            data-testid={`co-attach-remove-${att.id}`}
            disabled={busy}
            onClick={() => { void handleRemove(att.id) }}
            style={{
              marginLeft: 4,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--red)',
              fontSize: 11,
              padding: 0,
            }}
          >
            ×
          </button>
        </span>
      ))}
    </span>
  ) : null

  const previewModal = preview ? (
    <div
      data-testid="co-attach-preview"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={closePreview}
    >
      <div
        className="panel"
        style={{ width: 'min(720px, 96vw)', maxHeight: '90vh', padding: 14, background: 'var(--bg)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>{preview.name}</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <a
              className="btn b-ol btn-sm"
              href={preview.url}
              download={preview.name}
              data-testid="co-attach-preview-download"
            >
              Download
            </a>
            <button type="button" className="btn b-ol btn-sm" onClick={closePreview}>Close</button>
          </div>
        </div>
        {preview.kind === 'image' ? (
          <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
        ) : (
          <iframe title={preview.name} src={preview.url} style={{ width: '100%', height: '70vh', border: 'none' }} />
        )}
      </div>
    </div>
  ) : null

  if (chipsOnly) {
    return (
      <>
        {chips}
        {previewModal}
      </>
    )
  }

  return (
    <span
      style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}
      data-testid={`co-attach-field-${changeOrderId}`}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={ACCEPT}
        data-testid={`co-attach-input-${changeOrderId}`}
        onChange={(e) => { void onFile(e.target.files?.[0]) }}
      />
      <button
        type="button"
        className="btn b-ol btn-sm"
        data-testid={`co-attach-${changeOrderId}`}
        disabled={busy}
        aria-label="Attach file to change order"
        onClick={() => inputRef.current?.click()}
      >
        {busy ? '…' : attachments.length ? `File (${attachments.length})` : 'File'}
      </button>
      {chips}
      {err && <span style={{ color: 'var(--red)', fontSize: 10, maxWidth: 200, textAlign: 'right' }}>{err}</span>}
      {previewModal}
    </span>
  )
}
